/**
 * ResearchLightboxUniversal - DATA TRANSFORMATION LAYER (NO RESPONSIVE LOGIC)
 * 
 * This component is a clean data transformation layer between research data and the lightbox system.
 * 
 * ARCHITECTURE RULES:
 * - This component should NEVER contain responsive breakpoints (md:, lg:, sm:)
 * - All responsive behavior is controlled by TextContentLightbox (master)
 * - This component only handles data transformation and business logic
 * 
 * COMPONENT CHAIN:
 * ResearchLightboxUniversal (this) → PerplexityContentLightbox → TextContentLightbox (master)
 * 
 * RESPONSIBILITIES:
 * - Transform ResearchItem to PerplexityContent
 * - Handle PDF generation, navigation, delete/download logic
 * - Pass props to PerplexityContentLightbox
 * 
 * This is a GOOD EXAMPLE of clean architecture - no CSS classes, no responsive logic!
 */
import React from 'react';
import { PerplexityContentLightbox } from '@/components/perplexity-text-content';
import { PerplexityContent } from '@/components/perplexity-text-content/types/PerplexityContent';
import { ResearchItem } from '@/components/text-content-lightbox/types/ResearchLightboxTypes';
import { ResearchLightboxProps } from '@/components/text-content-lightbox/types/ResearchLightboxTypes';
import { TextContentType } from '@/components/text-content-lightbox/types/TextContentLightbox';
import { ResearchLibraryItem } from '@/hooks/research-generator/library/types';
import { useResearchPDF } from '@/hooks/pdf';

// Utility function to extract meaningful titles from URLs
const extractSourceTitle = (source: any): string => {
  // If source has a title property and it's not empty, use it
  if (typeof source === 'object' && source?.title && source.title.trim() !== '') {
    return source.title;
  }
  
  // Extract from URL - get domain name without www and extensions
  const url = typeof source === 'string' ? source : source?.url;
  if (!url) return 'Unknown Source';
  
  try {
    const domain = new URL(url).hostname;
    // Remove 'www.' prefix and get the main domain name
    const cleanDomain = domain.replace(/^www\./, '');
    // Split by dots and take the first part, then capitalize
    const siteName = cleanDomain.split('.')[0];
    return siteName.charAt(0).toUpperCase() + siteName.slice(1);
  } catch {
    // Fallback: extract from the URL path if domain parsing fails
    const cleanUrl = url.replace(/^https?:\/\//, '').replace(/^www\./, '');
    const firstPart = cleanUrl.split('/')[0].split('.')[0];
    return firstPart.charAt(0).toUpperCase() + firstPart.slice(1) || 'Source';
  }
};

// Transform ResearchItem to PerplexityContent
const transformResearchToPerplexity = (research: ResearchItem, researchDetails?: any): PerplexityContent => {
  // Extract research parameters from researchDetails if available
  const researchParameters = researchDetails?.research_parameters || {};
  
  return {
    id: research.id,
    title: research.title,
    content: research.content,
    contentType: 'research',
    sources: researchDetails?.sources?.map((source: any) => ({
      title: extractSourceTitle(source),
      url: typeof source === 'string' ? source : source.url,
      snippet: typeof source === 'string' ? undefined : source.snippet
    })) || [],
    metadata: {
      researchType: research.researchType || researchParameters.research_type,
      location: researchParameters.location,
      timeframe: researchParameters.timeframe,
      parameters: {
        // Core research parameters
        research_type: research.researchType || researchParameters.research_type,
        timeframe: researchParameters.timeframe,
        timeframe_label: researchParameters.timeframe_label,
        date_range_start: researchParameters.date_range_start,
        date_range_end: researchParameters.date_range_end,
        location: researchParameters.location,
        country: researchParameters.country,
        type_specific_filters: researchParameters.type_specific_filters,
        
        // Additional parameters from researchDetails
        ...researchParameters
      },
      relatedQuestions: researchDetails?.related_questions || [],
      createdAt: research.createdAt,
      userId: researchDetails?.user_id
    }
  };
};

// Transform array of ResearchItems to PerplexityContent
const transformResearchArray = (researchArray: ResearchItem[]): PerplexityContent[] => {
  return researchArray.map(research => transformResearchToPerplexity(research));
};

interface ResearchLightboxUniversalProps extends Omit<ResearchLightboxProps, 'research' | 'researchDetails'> {
  research: ResearchItem | null;
  allResearch?: ResearchLibraryItem[];
  researchDetails?: any;
  onRelatedQuestionClick?: (question: string) => void;
  isGeneratingRelatedQuestion?: boolean;
  onGenerationStateChange?: (isGenerating: boolean) => void;
  isOwner?: boolean;
  isDeleteLoading?: boolean;
  onDelete?: () => void;
  showFavoriteButton?: boolean; // Optional prop to hide favorite button
  contentType?: 'research' | 'private_research'; // NEW: Optional prop for content type
  showRelatedQuestions?: boolean; // NEW: Optional prop to hide related questions section
}

export const ResearchLightboxUniversal: React.FC<ResearchLightboxUniversalProps> = ({
  research,
  allResearch = [],
  isOpen,
  onClose,
  isLoading = false,
  researchDetails,
  onRelatedQuestionClick,
  // Navigation props
  hasPrevious = false,
  hasNext = false,
  isLoadingMore = false,
  hasNextPage = false,
  onPrevious,
  onNext,
  onLoadMore,
  // Generation state
  isGeneratingRelatedQuestion = false,
  onGenerationStateChange,
  // Delete functionality
  isOwner = false,
  isDeleteLoading = false,
  onDelete,
  showFavoriteButton = true, // Default to true for backward compatibility
  contentType = 'research', // Default to 'research' for backward compatibility
  showRelatedQuestions = true, // Default to true for backward compatibility
}) => {
  const { generatePDF } = useResearchPDF();
  
  // Transform research data to PerplexityContent with complete favorites integration
  const perplexityContent: PerplexityContent | null = research ? {
    id: research.id,
    title: research.title,
    content: research.content,
    contentType: (research.researchType || 'research') as TextContentType,
    sources: researchDetails?.sources?.map((source: any) => ({
      title: extractSourceTitle(source),
      url: typeof source === 'string' ? source : source.url,
      snippet: typeof source === 'string' ? undefined : source.snippet
    })) || [],
    metadata: {
      researchType: research.researchType,
      createdAt: research.createdAt,
      location: researchDetails?.research_parameters?.location,
      timeframe: researchDetails?.research_parameters?.timeframe,
      relatedQuestions: researchDetails?.related_questions || [],
      parameters: {
        ...researchDetails?.research_parameters,
        
        // ✅ CRITICAL: Properly include favorites data for seamless integration
        isFavorited: researchDetails?.is_favorited || false,
        favoriteCount: researchDetails?.favorite_count || 0,
      }
    }
  } : null;
  // Transform all research items with their individual favorites data
  const allPerplexityContent = allResearch.map(researchItem => {
    // For navigation items, we need to get their favorites data from the list
    // This ensures consistency between the list and lightbox
    return {
      id: researchItem.id,
      title: researchItem.title,
      content: researchItem.content,
      contentType: (researchItem.research_type || 'research') as TextContentType,
      sources: [], // Sources will be loaded when navigating to this item
      metadata: {
        researchType: researchItem.research_type,
        createdAt: researchItem.created_at,
        parameters: {
          // Use favorites data from the research list for consistency
          isFavorited: researchItem.is_favorited || false,
          favoriteCount: researchItem.favorite_count || 0,
        }
      }
    };
  });

  // Handle navigation
  const handleNavigate = (content: PerplexityContent) => {
    // Find the original research item and trigger navigation
    const originalResearch = allResearch.find(r => r.id === content.id);
    if (originalResearch && onNext) {
      // This is a simplified approach - in a real implementation,
      // you'd want to properly handle the navigation logic
      onNext();
    }
  };

  // Handle related question click
  const handleRelatedQuestionClick = (question: string) => {
    if (onRelatedQuestionClick) {
      onRelatedQuestionClick(question);
    }
  };

  // Handle delete
  const handleDelete = async (content: PerplexityContent) => {
    if (onDelete) {
      await onDelete();
    }
  };

  // Handle download
  const handleDownload = (content: PerplexityContent) => {
    // Generate PDF instead of markdown
    generatePDF(content);
  };

  // Check ownership
  const checkOwnership = async (content: PerplexityContent): Promise<boolean> => {
    return isOwner;
  };

  return (
    <PerplexityContentLightbox
      content={perplexityContent}
      isOpen={isOpen}
      onClose={onClose}
      allContent={allPerplexityContent}
      onNavigate={handleNavigate}
      loadMore={onLoadMore}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isLoadingMore}
      isLoading={isLoading}
      onRelatedQuestionClick={handleRelatedQuestionClick}
      onDelete={handleDelete}
      onDownload={handleDownload}
      checkOwnership={checkOwnership}
      isGeneratingRelatedQuestion={isGeneratingRelatedQuestion}
      onGenerationStateChange={onGenerationStateChange}
      showFavoriteButton={showFavoriteButton}
      contentType={contentType}
      showRelatedQuestions={showRelatedQuestions}
    />
  );
};

export default ResearchLightboxUniversal; 